import { apiConfig } from './config';
import type { ApiErrorBody } from './types';

export class ApiError extends Error {
  public status: number;
  public body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  get isUnauthorized() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isValidation() {
    return this.status === 422 || this.status === 400;
  }
}

type TokenProvider = () => string | null;

let tokenProvider: TokenProvider = () => null;
let onUnauthorized: (() => void) | null = null;

export function configureHttp(opts: {
  getToken?: TokenProvider;
  onUnauthorized?: () => void;
}) {
  if (opts.getToken) tokenProvider = opts.getToken;
  if (opts.onUnauthorized) onUnauthorized = opts.onUnauthorized;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Override base URL — useful for testing. */
  baseUrl?: string;
  /** When true, no Authorization header is attached even if a token exists. */
  anonymous?: boolean;
  /** Query string params. Falsy values are skipped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip automatic JSON parsing. */
  raw?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, opts: RequestOptions): string {
  const base = (opts.baseUrl || apiConfig.baseUrl).replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(base + cleanPath);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* ignore */
  }
  const message = body?.message || res.statusText || 'Request failed';
  return new ApiError(res.status, body, message);
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, anonymous, raw, query: _q, baseUrl: _b, headers, ...rest } = opts;
  void _q;
  void _b;

  const finalHeaders = new Headers(headers);
  if (!finalHeaders.has('Accept')) finalHeaders.set('Accept', 'application/json');

  let payload: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
      payload = body as BodyInit;
    } else {
      payload = JSON.stringify(body);
      if (!finalHeaders.has('Content-Type')) finalHeaders.set('Content-Type', 'application/json');
    }
  }

  if (!anonymous) {
    const token = tokenProvider();
    if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(buildUrl(path, opts), {
    ...rest,
    headers: finalHeaders,
    body: payload,
  });

  if (res.status === 401 && onUnauthorized) onUnauthorized();
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  if (raw) return res as unknown as T;

  const contentType = res.headers.get('Content-Type') || '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

export const http = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};
